import { useEffect, useRef, useState } from 'react';

const VERTEX_SHADER = `#version 300 es
precision highp float;

const vec2 positions[3] = vec2[](
  vec2(-1.0, -1.0),
  vec2( 3.0, -1.0),
  vec2(-1.0,  3.0)
);

out vec2 v_uv;

void main() {
  v_uv = positions[gl_VertexID] * 0.5 + 0.5;
  gl_Position = vec4(positions[gl_VertexID], 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_uv;
out vec4 fragColor;

uniform vec2 u_resolution;
uniform float u_time;
uniform vec2 u_mouse;

#define PI 3.14159265359
#define MAX_STEPS 96
#define MAX_DIST 12.0
#define SURF_DIST 0.002
#define SAMPLES 3

mat2 rot2(float a) {
  float s = sin(a), c = cos(a);
  return mat2(c, -s, s, c);
}

mat3 rotY(float a) {
  float s = sin(a), c = cos(a);
  return mat3(c, 0.0, s, 0.0, 1.0, 0.0, -s, 0.0, c);
}

mat3 rotX(float a) {
  float s = sin(a), c = cos(a);
  return mat3(1.0, 0.0, 0.0, 0.0, c, -s, 0.0, s, c);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float n = i.x + i.y * 57.0 + i.z * 113.0;
  float a = hash(vec2(n, 0.0));
  float b = hash(vec2(n + 1.0, 0.0));
  float c = hash(vec2(n + 57.0, 0.0));
  float d = hash(vec2(n + 58.0, 0.0));
  float e = hash(vec2(n + 113.0, 0.0));
  float f1 = hash(vec2(n + 114.0, 0.0));
  float g = hash(vec2(n + 170.0, 0.0));
  float h = hash(vec2(n + 171.0, 0.0));
  return mix(
    mix(mix(a, b, f.x), mix(c, d, f.x), f.y),
    mix(mix(e, f1, f.x), mix(g, h, f.x), f.y),
    f.z
  );
}

float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p *= 2.1;
    a *= 0.5;
  }
  return v;
}

float sdOctahedron(vec3 p, float s) {
  p = abs(p);
  return (p.x + p.y + p.z - s) * 0.57735027;
}

float sdCrystal(vec3 p) {
  p *= rotY(u_time * 0.15);
  p *= rotX(sin(u_time * 0.12) * 0.2);

  float shape = sdOctahedron(p, 1.2);

  // Faceting cuts
  vec3 q = p;
  q.yz = rot2(0.785398) * q.yz;
  shape = max(shape, -sdOctahedron(q - vec3(0.0, 0.0, 0.55), 0.7));

  q = p;
  q.xz = rot2(0.785398) * q.xz;
  shape = max(shape, -sdOctahedron(q - vec3(0.0, 0.0, 0.5), 0.65));

  // Slight internal displacement for caustic feel
  float displ = fbm(p * 2.5 + u_time * 0.15) * 0.08;
  return shape + displ;
}

float map(vec3 p) {
  return sdCrystal(p);
}

float raymarch(vec3 ro, vec3 rd) {
  float d0 = 0.0;
  for (int i = 0; i < MAX_STEPS; i++) {
    vec3 p = ro + rd * d0;
    float ds = map(p);
    d0 += ds;
    if (ds < SURF_DIST || d0 > MAX_DIST) break;
  }
  return d0;
}

vec3 normal(vec3 p) {
  vec2 e = vec2(0.001, 0.0);
  return normalize(vec3(
    map(p + e.xyy) - map(p - e.xyy),
    map(p + e.yxy) - map(p - e.yxy),
    map(p + e.yyx) - map(p - e.yyx)
  ));
}

vec3 background(vec3 rd) {
  vec3 top = vec3(1.0, 0.64, 0.12);
  vec3 bottom = vec3(0.12, 0.06, 0.02);
  float t = rd.y * 0.5 + 0.5;
  vec3 col = mix(bottom, top, t);
  // Soft amber glow in center
  col += vec3(1.0, 0.55, 0.05) * pow(max(0.0, dot(rd, normalize(vec3(0.0, 0.3, 1.0)))), 4.0) * 0.4;
  return col;
}

vec3 render(vec2 uv) {
  vec2 p = (uv - 0.5) * vec2(u_resolution.x / u_resolution.y, 1.0);

  vec3 ro = vec3(0.0, 0.0, -3.5);
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 ww = normalize(ta - ro);
  vec3 uu = normalize(cross(ww, vec3(0.0, 1.0, 0.0)));
  vec3 vv = cross(uu, ww);
  vec3 rd = normalize(p.x * uu + p.y * vv + 1.8 * ww);

  // Subtle mouse parallax
  rd.xy += (u_mouse - 0.5) * 0.03;
  rd = normalize(rd);

  float d = raymarch(ro, rd);

  vec3 col = background(rd);

  if (d < MAX_DIST) {
    vec3 pos = ro + rd * d;
    vec3 n = normal(pos);
    vec3 view = normalize(ro - pos);

    vec3 lightDir = normalize(vec3(0.6, 0.8, -0.4));
    vec3 lightCol = vec3(1.0, 0.78, 0.42);

    float diff = max(dot(n, lightDir), 0.0);
    float fresnel = pow(1.0 - max(dot(n, view), 0.0), 3.0);

    // Internal caustics
    float caust = fbm(pos * 4.0 + u_time * 0.2);
    caust = pow(caust, 2.0) * 1.2;

    // Amber body color with chromatic dispersion approximated per channel
    vec3 amber = vec3(1.0, 0.55, 0.0);
    vec3 amberDark = vec3(0.5, 0.2, 0.0);

    vec3 refractDir = refract(-view, n, 0.92);
    float innerLight = max(dot(refractDir, lightDir), 0.0);

    col = mix(amberDark, amber, diff + innerLight * 0.5);
    col += lightCol * (diff * 0.6 + fresnel * 0.9);
    col += vec3(1.0, 0.8, 0.3) * caust * (1.0 - fresnel);

    // Specular highlight
    vec3 halfVec = normalize(lightDir + view);
    float spec = pow(max(dot(n, halfVec), 0.0), 64.0);
    col += vec3(1.0) * spec * 0.7;

    // Reflection of background
    vec3 refl = reflect(-view, n);
    col += background(refl) * fresnel * 0.35;

    // Distance fog
    float fog = smoothstep(3.0, 10.0, d);
    col = mix(col, background(rd), fog);
  }

  // Vignette
  vec2 uvCenter = uv - 0.5;
  float vig = 1.0 - dot(uvCenter, uvCenter) * 0.8;
  col *= clamp(vig, 0.0, 1.0);

  // Film grain
  float grain = hash(uv * u_resolution + fract(u_time * 0.1));
  col += (grain - 0.5) * 0.03;

  return col;
}

void main() {
  // Very simple chromatic aberration: sample R/G/B at slightly different UVs
  vec3 col;
  vec2 off = (v_uv - 0.5) * 0.006;
  col.r = render(v_uv + off * 1.0).r;
  col.g = render(v_uv + off * 0.5).g;
  col.b = render(v_uv).b;

  col = pow(col, vec3(0.4545));
  fragColor = vec4(col, 1.0);
}
`;

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
}

function createProgram(gl, vsSource, fsSource) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link error: ${info}`);
  }
  return program;
}

export default function CrystalShader() {
  const canvasRef = useRef(null);
  const [webglFailed, setWebglFailed] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setWebglFailed(true);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      powerPreference: 'high-performance',
    });
    if (!gl) {
      setWebglFailed(true);
      return;
    }

    let program;
    try {
      program = createProgram(gl, VERTEX_SHADER, FRAGMENT_SHADER);
    } catch (err) {
      console.error(err);
      setWebglFailed(true);
      return;
    }

    const uTime = gl.getUniformLocation(program, 'u_time');
    const uResolution = gl.getUniformLocation(program, 'u_resolution');
    const uMouse = gl.getUniformLocation(program, 'u_mouse');

    let mouse = { x: 0.5, y: 0.5 };
    let animationId;
    let isVisible = true;
    let startTime = performance.now();

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    }

    function handleMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = (e.clientX - rect.left) / rect.width;
      mouse.y = 1.0 - (e.clientY - rect.top) / rect.height;
    }

    function render() {
      if (!isVisible) {
        animationId = requestAnimationFrame(render);
        return;
      }

      resize();

      const time = (performance.now() - startTime) * 0.001;

      gl.useProgram(program);
      gl.uniform1f(uTime, time);
      gl.uniform2f(uResolution, canvas.width, canvas.height);
      gl.uniform2f(uMouse, mouse.x, mouse.y);

      gl.drawArrays(gl.TRIANGLES, 0, 3);

      animationId = requestAnimationFrame(render);
    }

    resize();
    render();

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('resize', resize, { passive: true });

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { threshold: 0.01 }
    );
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resize);
      observer.disconnect();
      gl.deleteProgram(program);
    };
  }, []);

  if (webglFailed) {
    return (
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse at 50% 30%, #F59E0B 0%, #B45309 40%, #451a03 100%)',
        }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 -z-10 h-full w-full"
      aria-hidden="true"
    />
  );
}
